import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { styled } from '@mui/material';
import { selectAssignedProvidersCount } from '@provider-portal/common/experiments/account';
import type { MenuEditorParams } from '@provider-portal/common/experiments/providerOnboarding';
import {
  shouldEnableMenuEditor,
  SSU_EXPERIMENT_EVENTS,
  useMenuEditorExperimentEnabled,
  useShouldEnableMenuEditorParams,
} from '@provider-portal/common/experiments/providerOnboarding';
import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useIsOnboardingRoute } from '@provider-portal/common/routing/hooks';
import { onboardingRoutePrefix } from '@provider-portal/common/routing/routes';

import { ArrowBack } from '@bolteu/kalep-react-icons/dist';

import { useCommonAppServices } from 'root/CommonAppServicesContext';
import {
  selectIsSelectedProviderNewMerchantVenueInOnboarding,
  selectSelectedProvider,
} from 'root/rdx/account';

const StyledRouterLink = styled(RouterLink)({
  display: 'inline-flex',
  gap: '1em',
  padding: '0.5em 1em',
  borderRadius: '1em',
  border: '0.1em solid var(--color-content-secondary)',
  '&:hover': {
    borderColor: 'var(--color-content-action-secondary)',
  },
});

function useTrackMenuEditorMetadataEffect(menuEditorParams: MenuEditorParams): void {
  const { mixpanelService } = useCommonAppServices();
  const selectedProvider = useSelector(selectSelectedProvider);
  const accountProvidersCount = useSelector(selectAssignedProvidersCount);
  const isMenuEditorExperimentEnabled = useMenuEditorExperimentEnabled();

  useEffect(() => {
    if (!isMenuEditorExperimentEnabled) {
      return;
    }

    mixpanelService.trackProviderEvent(SSU_EXPERIMENT_EVENTS.LOADED, {
      providerId: selectedProvider.providerId,
      isDesktop: menuEditorParams.isDesktop,
      isTreatment: menuEditorParams.isWithMenuEditor,
      isOnboarding: menuEditorParams.isAccountOnboarding,
      hasMenuEditor: shouldEnableMenuEditor(menuEditorParams),
      providerSignupSource: selectedProvider.signup_source,
      accountProvidersCount,
    });
  }, [
    mixpanelService,
    menuEditorParams,
    accountProvidersCount,
    selectedProvider.providerId,
    selectedProvider.signup_source,
    isMenuEditorExperimentEnabled,
  ]);
}

function Ribbon(): React.ReactElement {
  const { formatMessage } = useIntl();
  const { mixpanelService, accountService } = useCommonAppServices();
  const selectedProviderId = useObservableAndState(accountService.getSelectedProviderId$());

  const trackGotoOnboarding = useCallback(() => {
    mixpanelService.trackProviderEvent(SSU_EXPERIMENT_EVENTS.RETURN_TO_ONBOARDING, {
      providerId: selectedProviderId,
    });
  }, [mixpanelService, selectedProviderId]);

  return (
    <StyledRouterLink to={onboardingRoutePrefix} onClick={trackGotoOnboarding}>
      <ArrowBack className="block" size="lg" />
      <span>{formatMessage({ id: 'experiments.menuEditor.returnToOnboardingV2' })}</span>
    </StyledRouterLink>
  );
}

export function OnboardingRibbon(): React.ReactElement {
  const isOnboardingRoute = useIsOnboardingRoute();
  const menuEditorParams = useShouldEnableMenuEditorParams();
  const shouldEnableMenuEditorForSSU = shouldEnableMenuEditor(menuEditorParams);
  const isNewMerchantVenueOnboarding = useSelector(
    selectIsSelectedProviderNewMerchantVenueInOnboarding,
  );
  const shouldEnableMerchantMenuEditor =
    shouldEnableMenuEditorForSSU || isNewMerchantVenueOnboarding;

  useTrackMenuEditorMetadataEffect(menuEditorParams);

  if (!shouldEnableMerchantMenuEditor) {
    return <></>;
  }

  if (isOnboardingRoute) {
    return <></>;
  }

  return <Ribbon />;
}
